import React from "react";

import { NamedIcon } from "~/components/ui/named-icon";
import { safeBuilderText } from "~/lib/builder-utils";
import { cn } from "~/lib/cn";

interface HeadingProps {
  preHeadline?: unknown;
  headline?: unknown;
  subheadline?: unknown;
  description?: unknown;
  className?: string;
  isH1?: boolean;
  align?: "center" | "left";
  iconName?: string;
}

interface HeadingElementProps {
  isH1?: boolean;
  className?: string;
  children: React.ReactNode;
}

export const HeadingElement = ({
  isH1,
  className,
  children,
  ...props
}: HeadingElementProps) => {
  if (isH1) {
    return (
      <h1 className={className} {...props}>
        {children}
      </h1>
    );
  }
  return (
    <h2 className={className} {...props}>
      {children}
    </h2>
  );
};

/** Marketing heading block: pre-headline, headline, subheadline and description. */
export const Heading = ({
  preHeadline,
  headline,
  subheadline,
  description,
  className,
  isH1,
  align = "center",
  iconName,
}: HeadingProps) => {
  const safePreHeadline = safeBuilderText(preHeadline);
  const safeHeadline = safeBuilderText(headline);
  const safeSubheadline = safeBuilderText(subheadline);
  const safeDescription = safeBuilderText(description);

  const centered = align === "center";

  return (
    <div className={cn(className, centered && "text-center")}>
      {iconName && (
        <div className={cn("mb-4", centered && "flex justify-center")}>
          <div className="inline-flex rounded-full bg-linear-to-br from-violet-100 to-purple-100 p-6 shadow-lg">
            <NamedIcon name={iconName} className="h-12 w-12 text-violet-600" />
          </div>
        </div>
      )}
      {safePreHeadline && (
        <p className="mb-1 text-sm font-medium tracking-wide text-purple-700 uppercase">
          {safePreHeadline}
        </p>
      )}
      {safeHeadline && (
        <HeadingElement
          isH1={isH1}
          className={cn(
            "text-text-primary mb-4 max-w-2xl font-heading text-3xl font-medium tracking-tight text-balance md:text-4xl",
            centered && "mx-auto",
          )}
        >
          {safeHeadline}
        </HeadingElement>
      )}
      {safeSubheadline && (
        <p
          className={cn(
            "mb-8 max-w-xl text-xl text-muted-foreground",
            centered && "mx-auto",
          )}
        >
          {safeSubheadline}
        </p>
      )}
      {safeDescription && (
        <p
          className={cn(
            "text-text-secondary text-lg leading-relaxed",
            centered && "mx-auto max-w-2xl",
          )}
        >
          {safeDescription}
        </p>
      )}
    </div>
  );
};
